import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/navigation/Sidebar';
import PatientContextBar from '../../components/navigation/PatientContextBar';
import QuickActions from '../../components/navigation/QuickActions';
import SessionManager from '../../components/navigation/SessionManager';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import LabResultsTable from './components/LabResultsTable';

import CriticalValueAlert from './components/CriticalValueAlert';
import ResultInterpretationPanel from './components/ResultInterpretationPanel';
import DiagnosticOrderPanel from './components/LabOrderPanel';
import DiagnosticTrendChart from './components/LabTrendChart';
import ResultComparisonView from './components/ResultComparisonView';
import RelatedDocuments from './components/RelatedDocuments';

const DiagnosticResults = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('all-results');
  const [selectedResult, setSelectedResult] = useState(null);
  const [dateRange, setDateRange] = useState('last-30-days');
  const [selectedTestsForChart, setSelectedTestsForChart] = useState(['Glucose', 'HbA1c']);

  const currentPatient = {
    name: "Sarah Mitchell",
    mrn: "MRN-2024-789456",
    dob: "03/15/1978",
    age: "48 years"
  };

  const patientAlerts = [
    {
      id: 1,
      severity: 'critical',
      title: 'Critical Diagnostic Value',
      message: 'INR level critically high at 4.5 - bleeding risk before dental procedures',
      actionRequired: 'Consult with physician before invasive procedures',
      timestamp: '10 minutes ago',
      dismissible: false
    },
    {
      id: 2,
      severity: 'warning',
      title: 'Abnormal Result',
      message: 'Glucose elevated at 180 mg/dL - may affect healing',
      timestamp: '2 hours ago',
      dismissible: true
    }
  ];

  const labResults = [
    {
      id: 1,
      testName: "Glucose (Fasting)",
      value: "180",
      referenceRange: "70-100",
      unit: "mg/dL",
      status: "High",
      severity: "abnormal",
      trend: "up",
      date: "01/14/2026"
    },
    {
      id: 2,
      testName: "INR",
      value: "4.5",
      referenceRange: "0.8-1.2",
      unit: "",
      status: "Critical High",
      severity: "critical",
      trend: "up",
      date: "01/14/2026"
    },
    {
      id: 3,
      testName: "Platelet Count",
      value: "245",
      referenceRange: "150-400",
      unit: "K/uL",
      status: "Normal",
      severity: "normal",
      trend: "stable",
      date: "01/14/2026"
    },
    {
      id: 4,
      testName: "Bleeding Time",
      value: "4.2",
      referenceRange: "2-7",
      unit: "min",
      status: "Normal",
      severity: "normal",
      trend: "stable",
      date: "01/14/2026"
    },
    {
      id: 5,
      testName: "Hemoglobin",
      value: "14.2",
      referenceRange: "13.5-17.5",
      unit: "g/dL",
      status: "Normal",
      severity: "normal",
      trend: "stable",
      date: "01/14/2026"
    },
    {
      id: 6,
      testName: "White Blood Cell Count",
      value: "7.8",
      referenceRange: "4.5-11.0",
      unit: "K/uL",
      status: "Normal",
      severity: "normal",
      trend: "stable",
      date: "01/14/2026"
    },
    {
      id: 7,
      testName: "Creatinine",
      value: "1.0",
      referenceRange: "0.7-1.3",
      unit: "mg/dL",
      status: "Normal",
      severity: "normal",
      trend: "stable",
      date: "01/14/2026"
    },
    {
      id: 8,
      testName: "HbA1c",
      value: "6.8",
      referenceRange: "4.0-5.6",
      unit: "%",
      status: "High",
      severity: "abnormal",
      trend: "stable",
      date: "01/14/2026"
    }
  ];

  const trendChartData = [
    { date: '12/15', Glucose: 142, HbA1c: 6.2, INR: 2.5, Platelet: 240 },
    { date: '12/22', Glucose: 155, HbA1c: 6.4, INR: 3.0, Platelet: 238 },
    { date: '12/29', Glucose: 168, HbA1c: 6.6, INR: 3.8, Platelet: 242 },
    { date: '01/05', Glucose: 175, HbA1c: 6.7, INR: 4.2, Platelet: 243 },
    { date: '01/14', Glucose: 180, HbA1c: 6.8, INR: 4.5, Platelet: 245 }
  ];

  const criticalAlerts = [
    {
      id: 1,
      testName: "INR",
      value: "4.5",
      unit: "",
      referenceRange: "0.8-1.2 (normal), 2.0-3.0 (therapeutic)",
      detectedTime: "01/14/2026 09:15 AM",
      clinicalSignificance: "Elevated INR indicates increased bleeding risk. Patient on anticoagulation therapy requires medical consultation before any invasive dental procedures including extractions, periodontal surgery, or implant placement. Consider postponing elective procedures until INR is within acceptable range."
    }
  ];

  const criticalAlertsState = criticalAlerts;

  const commonPanels = [
    {
      id: 1,
      name: "Pre-Surgical Panel",
      estimatedCost: "85.00",
      tests: [
        { id: 'pre-1', name: 'CBC' },
        { id: 'pre-2', name: 'PT/INR' },
        { id: 'pre-3', name: 'Platelet Count' },
        { id: 'pre-4', name: 'Bleeding Time' },
        { id: 'pre-5', name: 'Glucose' }
      ]
    },
    {
      id: 2,
      name: "Coagulation Panel",
      estimatedCost: "65.00",
      tests: [
        { id: 'coag-1', name: 'PT/INR' },
        { id: 'coag-2', name: 'PTT' },
        { id: 'coag-3', name: 'Platelet Count' },
        { id: 'coag-4', name: 'Bleeding Time' }
      ]
    },
    {
      id: 3,
      name: "Diabetes Monitoring",
      estimatedCost: "55.00",
      tests: [
        { id: 'dm-1', name: 'Fasting Glucose' },
        { id: 'dm-2', name: 'HbA1c' },
        { id: 'dm-3', name: 'Creatinine' }
      ]
    },
    {
      id: 4,
      name: "Infection Screening",
      estimatedCost: "75.00",
      tests: [
        { id: 'inf-1', name: 'WBC' },
        { id: 'inf-2', name: 'ESR' },
        { id: 'inf-3', name: 'CRP' },
        { id: 'inf-4', name: 'Blood Culture' }
      ]
    }
  ];

  const comparisonData = [
    { id: 1, testName: 'Glucose', current: 180, previous: 142, unit: 'mg/dL' },
    { id: 2, testName: 'HbA1c', current: 6.8, previous: 6.2, unit: '%' },
    { id: 3, testName: 'INR', current: 4.5, previous: 2.5, unit: '' },
    { id: 4, testName: 'Platelet', current: 245, previous: 240, unit: 'K/uL' }
  ];

  const relatedDocuments = [
    {
      id: 1,
      type: 'imaging',
      title: 'Panoramic Radiograph',
      description: 'Full mouth radiograph showing bone levels and tooth structure',
      date: '01/10/2026',
      provider: 'Dr. Robert Chen'
    },
    {
      id: 2,
      type: 'pathology',
      title: 'Biopsy Results - Oral Lesion',
      description: 'Histopathology report of oral soft tissue lesion',
      date: '12/28/2025',
      provider: 'Dr. Lisa Martinez'
    },
    {
      id: 3,
      type: 'clinical-note',
      title: 'Medical Consultation',
      description: 'Physician clearance for dental surgery with anticoagulation management recommendations',
      date: '01/08/2026',
      provider: 'Dr. James Wilson'
    }
  ];

  const resultInterpretation = selectedResult ? {
    testName: selectedResult?.testName,
    significance: `The ${selectedResult?.testName} level of ${selectedResult?.value} ${selectedResult?.unit} is ${selectedResult?.status?.toLowerCase()} compared to the reference range of ${selectedResult?.referenceRange} ${selectedResult?.unit}. This finding requires clinical correlation with patient symptoms and medical history.`,
    possibleCauses: selectedResult?.severity === 'critical' 
      ? [
          'Acute or chronic kidney disease',
          'Excessive potassium intake or supplementation',
          'Medications affecting potassium excretion (ACE inhibitors, ARBs, potassium-sparing diuretics)',
          'Metabolic acidosis',
          'Tissue breakdown or hemolysis'
        ]
      : selectedResult?.severity === 'abnormal'
      ? [
          'Uncontrolled diabetes mellitus',
          'Insulin resistance or insufficient insulin therapy',
          'Dietary non-compliance',
          'Medication non-adherence',
          'Concurrent illness or stress'
        ]
      : [
          'Normal physiological variation',
          'Adequate disease management',
          'Effective medication therapy'
        ],
    recommendedActions: selectedResult?.severity === 'critical'
      ? [
          'Immediate physician notification and patient assessment',
          'Repeat test to confirm result and rule out hemolysis',
          'ECG monitoring for cardiac arrhythmias',
          'Consider emergency treatment with calcium gluconate, insulin/glucose, or dialysis',
          'Review and adjust medications affecting potassium levels',
          'Dietary counseling on potassium restriction'
        ]
      : selectedResult?.severity === 'abnormal'
      ? [
          'Review and optimize diabetes medication regimen',
          'Reinforce dietary and lifestyle modifications',
          'Increase frequency of glucose monitoring',
          'Schedule follow-up appointment within 2-4 weeks',
          'Consider referral to endocrinology or diabetes educator',
          'Assess for complications of diabetes'
        ]
      : [
          'Continue current management plan',
          'Routine follow-up as scheduled',
          'Maintain healthy lifestyle practices'
        ],
    clinicalGuidelines: selectedResult?.severity === 'critical' ? 'Per American Heart Association guidelines, severe hyperkalemia (K+ >6.0 mEq/L) requires immediate treatment to prevent life-threatening cardiac arrhythmias. Treatment should be initiated while confirming the result with repeat testing.'
      : selectedResult?.severity === 'abnormal' ? 'According to American Diabetes Association standards, HbA1c target for most adults with diabetes is <7%. Values >8% indicate poor glycemic control and increased risk of microvascular and macrovascular complications.' : 'Result within normal limits. Continue routine monitoring per established care plan.'
  } : null;

  const dateRangeOptions = [
    { value: 'last-7-days', label: 'Last 7 Days' },
    { value: 'last-30-days', label: 'Last 30 Days' },
    { value: 'last-90-days', label: 'Last 90 Days' },
    { value: 'last-6-months', label: 'Last 6 Months' },
    { value: 'last-year', label: 'Last Year' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const testOptions = [
    { value: 'Glucose', label: 'Glucose' },
    { value: 'HbA1c', label: 'HbA1c' },
    { value: 'Potassium', label: 'Potassium' },
    { value: 'Creatinine', label: 'Creatinine' }
  ];

  const handleResultClick = (result) => {
    setSelectedResult(result);
  };

  const handleAlertAcknowledge = (alertId) => {
    console.log('Alert acknowledged:', alertId);
  };

  const handleAlertViewDetails = (alert) => {
    console.log('View alert details:', alert);
  };

  const handleOrderSubmit = (orderData) => {
    console.log('Lab order submitted:', orderData);
  };

  const handleDocumentClick = (document) => {
    console.log('Document clicked:', document);
  };

  const handleSessionExpired = () => {
    navigate('/login');
  };

  const handleSessionExtended = () => {
    console.log('Session extended');
  };

  const quickActions = [
    {
      id: 'order-lab',
      label: 'Order Lab',
      icon: 'FlaskConical',
      onClick: () => setActiveTab('order-labs'),
      shortcut: 'Ctrl+L'
    },
    {
      id: 'view-trends',
      label: 'View Trends',
      icon: 'TrendingUp',
      onClick: () => setActiveTab('trends'),
      shortcut: 'Ctrl+T'
    },
    {
      id: 'compare-results',
      label: 'Compare Results',
      icon: 'GitCompare',
      onClick: () => setActiveTab('comparison'),
      shortcut: 'Ctrl+C'
    },
    {
      id: 'print-results',
      label: 'Print Results',
      icon: 'Printer',
      onClick: () => console.log('Print results'),
      shortcut: 'Ctrl+P'
    }
  ];

  return (
    <div className="min-h-screen bg-background pt-16">
      <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />
      <div className="">
        <PatientContextBar 
          patient={currentPatient} 
          alerts={patientAlerts}
          onAlertClick={(alert) => console.log('Alert clicked:', alert)}
        />

        <main className="max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-3">
                  <Icon name="FlaskConical" size={32} className="text-primary" />
                  Diagnostic Results
                </h1>
                <p className="text-sm md:text-base text-muted-foreground mt-1">
                  Review diagnostic test results, track trends, and order pre-surgical testing
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/clinical-documentation')}
                  iconName="FileText"
                  iconPosition="left"
                >
                  Documentation
                </Button>
                <Button
                  variant="default"
                  onClick={() => setActiveTab('order-labs')}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Order Labs
                </Button>
              </div>
            </div>

            {criticalAlertsState?.length > 0 && (
              <CriticalValueAlert
                alerts={criticalAlertsState}
                onAcknowledge={handleAlertAcknowledge}
                onViewDetails={handleAlertViewDetails}
              />
            )}
          </div>

          <div className="mb-6">
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {[
                { id: 'all-results', label: 'All Results', icon: 'List' },
                { id: 'trends', label: 'Trends', icon: 'TrendingUp' },
                { id: 'comparison', label: 'Comparison', icon: 'GitCompare' },
                { id: 'interpretation', label: 'Interpretation', icon: 'BookOpen' },
                { id: 'order-labs', label: 'Order Labs', icon: 'FlaskConical' },
                { id: 'documents', label: 'Related Documents', icon: 'FolderOpen' }
              ]?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-2 md:py-3 rounded-lg text-sm md:text-base font-medium transition-smooth whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'all-results' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-card rounded-lg border border-border p-4 md:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <h2 className="text-lg md:text-xl font-semibold text-foreground">
                      Recent Lab Results
                    </h2>
                    <div className="w-full sm:w-48">
                      <Select
                        options={dateRangeOptions}
                        value={dateRange}
                        onChange={setDateRange}
                        placeholder="Select date range"
                      />
                    </div>
                  </div>
                  <LabResultsTable results={labResults} onResultClick={handleResultClick} />
                </div>
              </div>

              <div className="space-y-6">
                <ResultInterpretationPanel interpretation={resultInterpretation} />
              </div>
            </div>
          )}

          {activeTab === 'trends' && (
            <div className="space-y-6">
              <div className="bg-card rounded-xl border border-border p-4 md:p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Diagnostic Trends</h3>
                <DiagnosticTrendChart data={trendChartData} selectedTests={selectedTestsForChart} />
              </div>

              <div className="bg-card rounded-lg border border-border p-4 md:p-6">
                <h3 className="text-lg md:text-xl font-semibold text-foreground mb-4">
                  Trend Analysis Summary
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-muted rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="TrendingUp" size={20} color="var(--color-destructive)" />
                      <span className="text-sm font-semibold text-foreground">Increasing Trends</span>
                    </div>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>• Glucose: +9.9% over 30 days</li>
                      <li>• HbA1c: +9.0% over 30 days</li>
                      <li>• Potassium: +47.6% over 30 days (Critical)</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-muted rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Minus" size={20} color="var(--color-success)" />
                      <span className="text-sm font-semibold text-foreground">Stable Values</span>
                    </div>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>• Hemoglobin: Within normal range</li>
                      <li>• WBC Count: Within normal range</li>
                      <li>• Platelet Count: Within normal range</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'comparison' && (
            <div className="space-y-6">
              <ResultComparisonView comparisonData={comparisonData} />
              
              <div className="bg-card rounded-lg border border-border p-4 md:p-6">
                <h3 className="text-lg md:text-xl font-semibold text-foreground mb-4">
                  Statistical Analysis
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-destructive/10 rounded-lg border border-destructive">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
                      <span className="text-sm font-semibold text-foreground">Critical Changes</span>
                    </div>
                    <p className="text-2xl font-bold text-destructive mb-1">1</p>
                    <p className="text-xs text-muted-foreground">Potassium +47.6%</p>
                  </div>
                  <div className="p-4 bg-warning/10 rounded-lg border border-warning">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
                      <span className="text-sm font-semibold text-foreground">Significant Changes</span>
                    </div>
                    <p className="text-2xl font-bold text-warning mb-1">2</p>
                    <p className="text-xs text-muted-foreground">Glucose +9.9%, HbA1c +9.0%</p>
                  </div>
                  <div className="p-4 bg-success/10 rounded-lg border border-success">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                      <span className="text-sm font-semibold text-foreground">Stable Values</span>
                    </div>
                    <p className="text-2xl font-bold text-success mb-1">1</p>
                    <p className="text-xs text-muted-foreground">Creatinine +20.0%</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'interpretation' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-card rounded-lg border border-border p-4 md:p-6">
                <h2 className="text-lg md:text-xl font-semibold text-foreground mb-4">
                  Select Result for Interpretation
                </h2>
                <div className="space-y-2">
                  {labResults?.filter(r => r?.severity !== 'normal')?.map((result) => (
                    <button
                      key={result?.id}
                      onClick={() => handleResultClick(result)}
                      className={`flex items-center justify-between w-full p-3 md:p-4 rounded-lg border transition-smooth ${
                        selectedResult?.id === result?.id
                          ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon
                          name={result?.severity === 'critical' ? 'AlertCircle' : 'AlertTriangle'}
                          size={20}
                          color={result?.severity === 'critical' ? 'var(--color-destructive)' : 'var(--color-warning)'}
                        />
                        <div className="text-left">
                          <p className="text-sm md:text-base font-medium text-foreground">
                            {result?.testName}
                          </p>
                          <p className="text-xs md:text-sm text-muted-foreground">
                            {result?.value} {result?.unit} ({result?.status})
                          </p>
                        </div>
                      </div>
                      <Icon name="ChevronRight" size={18} className="text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </div>

              <ResultInterpretationPanel interpretation={resultInterpretation} />
            </div>
          )}

          {activeTab === 'order-labs' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <DiagnosticOrderPanel commonPanels={commonPanels} onOrderSubmit={handleOrderSubmit} />
              
              <div className="space-y-6">
                <div className="bg-card rounded-lg border border-border p-4 md:p-6">
                  <h3 className="text-lg md:text-xl font-semibold text-foreground mb-4">
                    Recent Orders
                  </h3>
                  <div className="space-y-3">
                    {[
                      { id: 1, panel: 'Basic Metabolic Panel', status: 'Completed', date: '01/14/2026' },
                      { id: 2, panel: 'Complete Blood Count', status: 'Completed', date: '01/14/2026' },
                      { id: 3, panel: 'Lipid Panel', status: 'Pending', date: '01/10/2026' }
                    ]?.map((order) => (
                      <div key={order?.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <div>
                          <p className="text-sm md:text-base font-medium text-foreground">{order?.panel}</p>
                          <p className="text-xs md:text-sm text-muted-foreground">{order?.date}</p>
                        </div>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            order?.status === 'Completed'
                              ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'
                          }`}
                        >
                          {order?.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-card rounded-lg border border-border p-4 md:p-6">
                  <h3 className="text-lg md:text-xl font-semibold text-foreground mb-4">
                    Insurance Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Primary Insurance</span>
                      <span className="text-sm font-medium text-foreground">Blue Cross Blue Shield</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Policy Number</span>
                      <span className="text-sm font-medium text-foreground">BCBS-789456123</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Pre-Auth Status</span>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                        Verified
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <RelatedDocuments documents={relatedDocuments} onDocumentClick={handleDocumentClick} />
              
              <div className="bg-card rounded-lg border border-border p-4 md:p-6">
                <h3 className="text-lg md:text-xl font-semibold text-foreground mb-4">
                  Document Categories
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Imaging', count: 12, icon: 'Image' },
                    { label: 'Pathology', count: 5, icon: 'Microscope' },
                    { label: 'Clinical Notes', count: 28, icon: 'FileText' },
                    { label: 'Reports', count: 15, icon: 'FileBarChart' }
                  ]?.map((category) => (
                    <button
                      key={category?.label}
                      className="flex flex-col items-center justify-center p-4 bg-muted rounded-lg hover:bg-muted/80 transition-smooth"
                    >
                      <Icon name={category?.icon} size={24} color="var(--color-primary)" />
                      <span className="text-sm font-medium text-foreground mt-2">{category?.label}</span>
                      <span className="text-xs text-muted-foreground">{category?.count} documents</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
      <QuickActions actions={quickActions} position="bottom-right" />
      <SessionManager
        sessionTimeout={900000}
        warningTime={120000}
        onSessionExpired={handleSessionExpired}
        onSessionExtended={handleSessionExtended}
      />
    </div>
  );
};

export default DiagnosticResults;